<template>
    <div class="container mx-auto px-4">
        <div class="flex my-6 md:my-12">
            <img class="mr-5 size-10 hidden md:inline-block" src="@/assets/img/bookend.png" />

            <div class="flex-grow">
                <div class="flex flex-col items-start sm:flex-row">
                    <div v-if="!isEmbed" class="flex flex-col sm:order-2">
                        <p class="text-xs text-gray-600 text-center">Scan to view content<br/>on RNAiScience.com</p>
                        <img src="@/assets/img/qr-code-rnai-science-ph1.png" alt="Content QR Code" class="w-32" />
                    </div>

                    <div class="flex-1">
                        <h1 class="text-2xl md:text-5xl font-semibold mb-3">
                            Overview of Selected Primary Hyperoxaluria Type 1 (PH1) Case Studies
                        </h1>
                        <h3 class="text-base md:text-2xl font-semibold text-lightblue mb-6">
                            These case studies demonstrate that the PH1 patient profile is variable<sup>1–6</sup>
                        </h3>
                    </div>
                </div>
                <h2 class="hidden md:block text-lg font-light mt-12 mb-6">PH1 CAN BE DIAGNOSED IN:</h2>

                <div class="grid grid-cols-3 gap-4 mb-6 md:mb-12">
                    <div class="col-span-3">
                        <!-- Grid-based UL to display LI elements in 4 columns -->
                        <ul class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 mt-6">
                            <li class="flex items-center">
                                <img class="size-14" src="@/assets/img/children-and-adults.svg" alt="Person Icon" />
                                <h3 class="font-semibold pl-4 flex-grow">Children and Adults</h3>
                            </li>
                            <li class="flex items-center">
                                <img class="size-14" src="@/assets/img/hemodialysis.svg" alt="Hemodialysis" />
                                <h3 class="font-semibold pl-4 flex-grow">Patients receiving hemodialysis</h3>
                            </li>
                            <li class="flex items-center">
                                <img class="size-14" src="@/assets/img/transplant-recipients.svg" alt="Kidneys Light Blue Icon" />
                                <h3 class="font-semibold pl-4 flex-grow">Transplant recipients</h3>
                            </li>
                            <li class="flex items-center">
                                <img class="size-14" src="@/assets/img/family-history.svg" alt="Family History" />
                                <h3 class="font-semibold pl-4 flex-grow">
                                    Patients with a family history of kidney impairment or stones
                                </h3>
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div class="col-span-1 md:col-span-2">
                        <div class="bg-grey p-5 h-full">
                            <h2 class="text-base md:text-xl text-lightblue font-semibold">
                                PH1 has high diagnostic delay; it is important to suspect in:<sup>6</sup>
                            </h2>

                            <ul class="pt-3 list-none list-inside font-light">
                                <li
                                    class="relative pl-6 before:content-['•'] before:absolute before:left-0 before:top-0"
                                >
                                    Those with recurrent nephrolithiasis
                                </li>
                                <li
                                    class="relative pl-6 before:content-['•'] before:absolute before:left-0 before:top-0"
                                >
                                    Those with nephrocalcinosis
                                </li>
                                <li
                                    class="relative pl-6 before:content-['•'] before:absolute before:left-0 before:top-0"
                                >
                                    Those with reduced kidney function or kidney failure at any age with a history of
                                    kidney stones or nephrocalcinosis
                                </li>
                                <li
                                    class="relative pl-6 before:content-['•'] before:absolute before:left-0 before:top-0"
                                >
                                    A child with a first kidney stone
                                </li>
                                <li
                                    class="relative pl-6 before:content-['•'] before:absolute before:left-0 before:top-0"
                                >
                                    A child younger than 12 months with failure to thrive and impaired kidney function
                                </li>
                                <li
                                    class="relative pl-6 before:content-['•'] before:absolute before:left-0 before:top-0"
                                >
                                    Adults with UOx excretion exceeding 75 mg/day without bowel dysfunction<sup>7</sup>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div class="bg-lightblue bg-opacity-50 p-12 flex flex-col justify-center">
                        <p class="text-center font-light">
                            Once PH1 is suspected, it is recommended by OxalEurope, ERKNet and AUA to refer for
                            <span class="font-semibold">plasma or urine oxalate assessments</span> and subsequent
                            <span class="font-semibold">genetic testing<sup>7,8</sup></span>
                        </p>
                    </div>
                </div>

                <div class="inline-flex flex-col md:flex-row gap-4 mt-6">
                    <button
                        class="bg-green py-4 px-8 mt-8 shadow rounded-full text-center text-gray-800 font-semibold text-sm flex items-center justify-center gap-2 flex-grow sm:flex-grow-0"
                        @click="openModal"
                    >
                        <span>Primary clinical manifestations of PH1</span>
                        <img class="inline-block h-4 w-4 font-semibold" src="@/assets/img/plus.svg" alt="Plus Icon" />
                    </button>
                    <button
                        class="bg-green py-4 px-8 mt-8 shadow rounded-full text-center text-gray-800 font-semibold text-sm flex items-center justify-center gap-2 flex-grow sm:flex-grow-0"
                        @click="openModal2"
                    >
                        <span>Diagnostic Algorithm</span>
                        <img class="inline-block h-4 w-4 font-semibold" src="@/assets/img/plus.svg" alt="Plus Icon" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Content  -->
        <div class="flex my-6 md:my-12">
            <img class="mr-5 size-10 hidden md:inline-block" src="@/assets/img/bookend.png" />

            <div class="flex-grow">
                <h2 class="text-2xl md:text-5xl font-semibold mb-3">Contents</h2>
                <h3 class="text-base md:text-2xl font-semibold text-lightblue mb-6">
                    Click to explore each case study
                </h3>

                <div class="grid md:grid-cols-2 gap-6 md:gap-10">
                    <ContentCallout
                        v-for="study in caseStudies"
                        :key="study.id"
                        :accent="study.accent"
                        :title="study.title"
                        :subtitle="study.subtitle"
                        :icon="study.icon"
                        :icon-alt="study.iconAlt"
                        :study-id="study.id"
                        class="callout"
                    />
                </div>
            </div>
        </div>

        <div class="my-6 md:my-12">
            <p class="text-xs mt-2 font-light">
                AUA, American Urological Association; ERKNet, European Rare Kidney Disease Reference Network; PH1,
                primary hyperoxaluria type 1; UOx urinary oxalate.<br />
                1. Poyah P et al. <span class="italic">Can J Kidney Health Dis</span> 2021;8:20543581211058931; 2. Cai Z
                et al. <span class="italic">BMC Nephrol</span> 2021;22:393; 3. Abukhatwah MW et al.
                <span class="italic">Medicine (Baltimore)</span> 2020;99:e20371; <br />
                4. Rather JI et al. <span class="italic">J Med Case Rep</span> 2023;17:421; 5. Julaina AJ et al.
                <span class="italic">J Clin Case Rep</span> 2019;9:8; 6. Milliner DS et al.
                <span class="italic">GeneReviews®</span> [updated February 10, 2022]. Available at:
                <a href="https://www.ncbi.nlm.nih.gov/books/NBK1283" target="_blank">
                    https://www.ncbi.nlm.nih.gov/books/NBK1283
                </a>
                (accessed July 2024); 7. Pearle MS et al. <span class="italic">J Urol</span> 2014;192:316; 8. Groothoff
                JW et al. <span class="italic">Nat Rev Nephrol</span> 2023;19:194–211.
            </p>
        </div>
        <ContentModal v-if="isModalVisible" :cta="PrimaryClinicalManifestationsData" @close="closeModal" />
        <ContentModal v-if="isModal2Visible" :cta="DiagnosticAlgorithmData" @close="closeModal2" />
    </div>
</template>

<script setup>
import ContentCallout from '@/components/ContentCallout.vue';
import { studies } from '@/content/studies';
import { ref, computed, reactive } from 'vue';
import ContentModal from '@/components/ContentModal.vue';
import PrimaryClinicalManifestations from '@/components/modals/PrimaryClinicalManifestations.vue';
import DiagnosticAlgorithm from '@/components/modals/DiagnosticAlgorithm.vue';

const isModalVisible = ref(false);
const isModal2Visible = ref(false);

const PrimaryClinicalManifestationsData = {
    title: 'Primary Clinical Manifestations of PH1 Can Look Different in Different Populations',
    subtitle: 'Relative Incidence (%) and Typical Features of PH1 Phenotypes at Initial Presentation',
    slides: [
        {
            title: 'Primary Clinical Manifestations of PH1 Can Look Different in Different Populations',
            subtitle: 'Relative Incidence (%) and Typical Features of PH1 Phenotypes at Initial Presentation',
            content: PrimaryClinicalManifestations,
            references:
                'CaOx, calcium oxalate; CKD, chronic kidney disease; KTx, kidney transplant; PH1, primary hyperoxaluria type 1. <br />1. Milliner DS et al. <span class="italic">GeneReviews®</span> [updated February 10, 2022]. Available at:  <a href="https://www.ncbi.nlm.nih.gov/books/NBK1283" target="_blank">https://www.ncbi.nlm.nih.gov/books/NBK1283</a> (accessed July 2024); <br />2. Hoppe B & Langman C. <span class="italic">Pediatr Nephrol</span> 2003;18:986–991;  3. Cochat P et al. <span class="italic">J Pediatr</span> 1999;135:746–750.',
            nextButtonIcon: null,
            nextButtonTitle: '',
            previousButtonTitle: 'Diagnostic Delay',
        },
    ],
};

const DiagnosticAlgorithmData = {
    title: 'Primary Clinical Manifestations of PH1 Can Look Different in Different Populations',
    subtitle: 'Relative Incidence (%) and Typical Features of PH1 Phenotypes at Initial Presentation',
    slides: [
        {
            title: 'PH1 Diagnostic Algorithm',
            subtitle: 'Published Recommendations for the Diagnosis of Patients with Suspected PH<sup>1–3</sup>',
            content: DiagnosticAlgorithm,
            references:
                '<span class="font-semibold">This information is being provided for educational purposes only. It is not intended to replace the independent medical judgment of any healthcare professional.</span> <br />Note that PH includes three types: PH1, PH2, and PH3. <br />AGXT, alanine-glyoxylate aminotransferase; CaOx, calcium oxalate; GRHPR, glyoxylate reductase/hydroxypyruvate reductase; HOGA1, 4-hydroxy-2-oxoglutarate aldolase; PH, primary hyperoxaluria;  *For use in patients with residual urine output;<sup>3 †</sup>  As patients on dialysis without PH can have average POx levels between 50 and 60 µmol/L,  patients on dialysis with PH may have even higher values.<sup>3</sup>  PH1, PH type 1; PH2, PH type 2; PH3, PH type 3; POx, plasma oxalate;  UOx, urinary oxalate; UOx:Cr, urinary oxalate to creatinine ratio. <br />1. Michael M et al. <span class="italic">Pediatr Nephrol</span>. 2024;39:3143–3155;  2. Edvardsson VO et al. <span class="italic">Pediatr Nephrol</span>. 2013;28:1923–1942;  3. Groothoff JW et al. <span class="italic">Nat Rev Nephrol</span>. 2023;19:194–211.',
            nextButtonIcon: null,
            nextButtonTitle: '',
            previousButtonTitle: '',
        },
    ],
};

const openModal2 = () => {
    isModal2Visible.value = true;
};

const closeModal2 = () => {
    isModal2Visible.value = false;
};
const openModal = () => {
    isModalVisible.value = true;
};

const closeModal = () => {
    isModalVisible.value = false;
};

const caseStudies = reactive(studies);

const isEmbed = computed(() => {
    return process.env.VUE_APP_IS_EMBED === 'true';
})
</script>

<style lang="css" scoped>
.callout {
    min-height: 331px;
}
</style>
